/**
 * Learn & Practice Landing Page
 * Main landing page with featured carousel, intentions grid, trusted ritualists,
 * and filterable self-guided rituals grid
 */

import { useQuery } from "@tanstack/react-query"
import { MapPin, Search, X } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import type { RitualCardProps } from "@/components/cards/ritualCard"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { Breadcrumbs } from "@/components/shared/Breadcrumbs"
import { PriceFilterPopover } from "@/components/shared/PriceFilterPopover"
import { RadioPopover } from "@/components/shared/RadioPopover"
import { SingleToggleChipPopover } from "@/components/shared/SingleToggleChipPopover"
import { ToggleChipPopover } from "@/components/shared/ToggleChipPopover"
import { useAuthStore } from "@/features/auth/store/authStore"
import { getIntentions } from "@/features/catalog/services/catalog.service"
import { FeaturedCarousel } from "@/features/explore/components/FeaturedCarousel"
import { RitualGrid } from "@/features/explore/components/RitualGrid"
import { IntentionsGrid } from "@/features/learn-practice/components/IntentionsGrid"
import { TrustedRitualists } from "@/features/learn-practice/components/TrustedRitualists"
import { useExploreRituals } from "@/features/learn-practice/hooks/useExploreRituals"
import { useFeaturedRituals } from "@/features/learn-practice/hooks/useFeaturedRituals"
import { useToggleSaveRitual } from "@/features/learn-practice/hooks/useToggleSaveRitual"
import { useTrustedRitualists } from "@/features/learn-practice/hooks/useTrustedRitualists"
import type { ExploreRitualsFilters, RitualType } from "@/features/learn-practice/types"
import { RITUAL_FORMAT_LABELS } from "@/features/learn-practice/types"
import { getMeetingPlatform } from "@/features/learn-practice/utils/meetingPlatform"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { useIntentions } from "@/features/profile/hooks/useIntentions"
import { getSpiritualTraditions } from "@/features/profile/services/user.service"
import { useBreakpointItems } from "@/hooks/useBreakpoints"
import { useDebounce } from "@/hooks/useDebounce"
import { useIsMobile } from "@/hooks/useIsMobile"
import { getAssetUrl } from "@/utils/assets"

function formatDuration(minutes: number | string | null | undefined): string {
  if (!minutes) return ""
  const mins = typeof minutes === "string" ? parseInt(minutes, 10) : minutes
  if (Number.isNaN(mins) || mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`
}

// Intention card cycle for xl+ (4-col grid, repeats every 8)
// Row 1: intension_1, olive, intension_2, water
// Row 2: terracotta, intension_4, water, intension_3
const INTENTION_CYCLE_XL = [
  { image: getAssetUrl("intension_1.jpg") },
  { color: "#5A5B3F" },
  { image: getAssetUrl("intension_2.jpeg") },
  { color: "#344653" },
  { color: "#C86757" },
  { image: getAssetUrl("intension_4.jpg") },
  { color: "#344653" },
  { image: getAssetUrl("intension_3.jpg") },
]

// Intention card cycle for <xl (2-col grid, repeats every 6)
const INTENTION_CYCLE_SM = [
  { image: getAssetUrl("intension_1.jpg") },
  { color: "#5A5B3F" },
  { color: "#344653" },
  { image: getAssetUrl("intension_2.jpeg") },
  { color: "#C86757" },
  { color: "#344653" },
]

// Filter definitions -- only include filters that have backend support
const FILTER_OPTIONS = [
  {
    key: "sort",
    label: "Sort by",
    options: ["newest", "trending"],
    displayLabels: { newest: "Newest", trending: "Trending" },
  },
  {
    key: "price",
    label: "Price",
    options: ["free", "paid"],
    displayLabels: { free: "Free", paid: "Paid" },
  },
  {
    key: "format",
    label: "Format",
    options: ["IN_PERSON", "VIRTUAL_COURSE", "SELF_GUIDED", "SELF_GUIDED_COURSE"],
    displayLabels: RITUAL_FORMAT_LABELS as Record<string, string>,
  },
  {
    key: "duration",
    label: "Duration",
    options: ["short", "medium", "long"],
    displayLabels: {
      short: "Short (under 30 min)",
      medium: "Medium (30-60 min)",
      long: "Long (60+ min)",
    },
  },
]

// Sort options as {value, label} for RadioPopover
const SORT_RADIO_OPTIONS = FILTER_OPTIONS[0].options.map(v => ({
  value: v,
  label: FILTER_OPTIONS[0].displayLabels[v] ?? v,
}))

const FORMAT_CHIP_OPTIONS = FILTER_OPTIONS[2].options.map(v => ({
  value: v,
  label: FILTER_OPTIONS[2].displayLabels[v] ?? v,
}))

const DURATION_CHIP_OPTIONS = FILTER_OPTIONS[3].options.map(v => ({
  value: v,
  label: FILTER_OPTIONS[3].displayLabels[v] ?? v,
}))

export function LearnAndPracticeScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  useEffect(() => {
    const scrollTarget = location.state?.scrollTo as string | undefined
    if (scrollTarget) {
      const timer = setTimeout(() => {
        document.getElementById(scrollTarget)?.scrollIntoView({ behavior: "smooth" })
        window.history.replaceState({}, "")
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [location.state])
  const isXL = useBreakpointItems({ sm: 0, md: 0, lg: 0, xl: 1 }) === 1
  const isMobile = useIsMobile()
  const intentionCycle = isXL ? INTENTION_CYCLE_XL : INTENTION_CYCLE_SM
  const { intentions: apiIntentions } = useIntentions(user?.id ?? "")
  const [locationDisplay, setLocationDisplay] = useState(
    user?.city ? `${user.city}${user.state ? `, ${user.state}` : ""}` : "Santa Monica",
  )
  const defaultLocation = user?.city
    ? `${user.city}${user.state ? `, ${user.state}` : ""}`
    : "Santa Monica"

  const handleLocationSelect = useCallback(
    (loc: { city?: string; state?: string; country?: string }) => {
      setLocationDisplay(
        loc.city
          ? `${loc.city}${loc.state ? `, ${loc.state}` : ""}`
          : loc.state
            ? `${loc.state}${loc.country ? `, ${loc.country}` : ""}`
            : (loc.country ?? ""),
      )
      setActiveFilters(prev => {
        return {
          ...prev,
          city: loc.city,
          state: loc.state,
          country: loc.country,
        }
      })
    },
    [],
  )

  const handleLocationClear = useCallback(() => {
    setLocationDisplay(defaultLocation)
    setActiveFilters(prev => {
      const { city, state, country, ...rest } = prev
      return rest
    })
  }, [defaultLocation])

  // Fetch filter option lists
  const { data: intentionsList = [] } = useQuery({
    queryKey: ["lp-filter-intentions", user?.id],
    queryFn: () => getIntentions(user?.id ?? ""),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  })
  const { data: traditionsList = [] } = useQuery({
    queryKey: ["lp-filter-spiritual-traditions"],
    queryFn: getSpiritualTraditions,
    staleTime: 5 * 60 * 1000,
  })

  // Map API intentions to grid format
  const intentionsData = useMemo(
    () =>
      apiIntentions.length > 0
        ? apiIntentions.map((i: { id: string; title: string; slug?: string }, index: number) => {
            const pos = index % intentionCycle.length
            const cell = intentionCycle[pos]
            return {
              id: i.id,
              name: i.title,
              image: cell.image,
              color: cell.color,
              isSolid: !cell.image,
            }
          })
        : [],
    [apiIntentions, intentionCycle],
  )

  // Search and filter state
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebounce(searchInput, 300)
  const [activeFilters, setActiveFilters] = useState<ExploreRitualsFilters>({
    sort: "newest",
  })

  // Build filters for the API
  const filters: ExploreRitualsFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      sort: activeFilters.sort || "newest",
      price: activeFilters.price,
      priceRange: activeFilters.priceRange,
      format: activeFilters.format,
      duration: activeFilters.duration,
      city: activeFilters.city,
      state: activeFilters.state,
      country: activeFilters.country,
    }),
    [debouncedSearch, activeFilters],
  )

  // API hooks
  const {
    rituals: featuredRituals,
    isLoading: featuredLoading,
    error: featuredError,
  } = useFeaturedRituals(5)
  const {
    ritualists: trustedRitualists,
    isLoading: ritualistsLoading,
    error: ritualistsError,
  } = useTrustedRitualists(5)
  const {
    rituals: exploreRituals,
    isLoading: exploreLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: exploreError,
  } = useExploreRituals(filters)
  const toggleSave = useToggleSaveRitual()
  const { follow, unfollow } = useFollowActions()

  // Stable bookmark handler -- toggleSave.mutate is referentially stable from useMutation
  const handleBookmark = useCallback(
    (ritualId: string) => toggleSave.mutate(ritualId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toggleSave.mutate],
  )

  // Map featured rituals to RitualCardProps
  const featuredCardData: RitualCardProps[] = useMemo(
    () =>
      featuredRituals.map(r => ({
        id: r.id,
        image: r.coverImage ?? undefined,
        userAvatar: r.user?.profilePictureUrl ?? undefined,
        username: r.user?.name ?? "Unknown",
        userHandle: r.user?.username ? `@${r.user.username}` : "",
        title: r.title,
        tags: [
          RITUAL_FORMAT_LABELS[r.ritualType] ?? r.ritualType,
          r.duration ? formatDuration(r.duration) : "",
        ].filter(Boolean),
        duration: r.duration ? formatDuration(r.duration) : "",
        isBookmarked: r.isSaved,
        onBookmarkClick: () => handleBookmark(r.id),
        userId: r.user?.id,
        price: r.fee ?? undefined,
        participationFee: r.isDonationBased ? "Donation-based" : r.isPaid ? "Paid" : "Free",
      })),
    [featuredRituals, handleBookmark],
  )

  // Map explore rituals to RitualCardProps
  const exploreCardData: RitualCardProps[] = useMemo(
    () =>
      exploreRituals.map(r => {
        const formatLabel = RITUAL_FORMAT_LABELS[r.ritualType] ?? r.ritualType
        const durationStr = r.duration ? formatDuration(r.duration) : ""
        const meetingPlatform = getMeetingPlatform(r.meetingLink)
        const locationStr =
          r.ritualType === "IN_PERSON" && r.city
            ? `${durationStr}${r.ritualTs ? ` - ${new Date(r.ritualTs).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""} @ ${r.city}${r.state ? `, ${r.state}` : ""}`
            : r.ritualType === "VIRTUAL_COURSE" && r.hasMeetingLink
              ? `${durationStr}${r.ritualTs ? ` - ${new Date(r.ritualTs).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""} @ ${meetingPlatform}`
              : durationStr

        return {
          id: r.id,
          image: r.coverImage ?? undefined,
          userAvatar: r.user?.profilePictureUrl ?? undefined,
          username: r.user?.name ?? "Unknown",
          userHandle: r.user?.username ? `@${r.user.username}` : "",
          title: r.title,
          tags: [formatLabel, locationStr].filter(Boolean),
          duration: durationStr,
          isBookmarked: r.isSaved,
          onBookmarkClick: () => handleBookmark(r.id),
          userId: r.user?.id,
          price: r.fee ?? undefined,
          participationFee: r.isDonationBased ? "Donation-based" : r.isPaid ? "Paid" : "Free",
        }
      }),
    [exploreRituals, handleBookmark],
  )

  const handleResetFilters = useCallback(() => {
    setActiveFilters({ sort: "newest" })
    setSearchInput("")
    setLocationDisplay(defaultLocation)
  }, [defaultLocation])

  const hasActiveFilters =
    Object.keys(activeFilters).some(k => k !== "sort") ||
    searchInput.length > 0 ||
    !!activeFilters.city ||
    !!activeFilters.state ||
    !!activeFilters.country

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="md:pt-6 flex flex-col gap-7.5 max-w-360 mx-auto px-5 md:px-17.5">
        {!isMobile && (
          <Breadcrumbs items={[{ label: "Home", path: "/" }, { label: "Learn & Practice" }]} />
        )}
        <div className="flex flex-col items-start">
          <h1 className="text-[20px] md:text-[30px] text-color-brand-offwhite uppercase leading-normal font-semibold">
            Learn & Practice
          </h1>
          {isMobile ? (
            <p className="text-color-brand-offwhite text-[13px] leading-5 md:text-sm md:leading-6.25 opacity-90 mt-1.25 md:mt-0">
              Explore practices, rituals, and guidance.
            </p>
          ) : (
            <p className="text-color-brand-offwhite text-sm leading-6.25 opacity-90 mt-1.25 md:mt-0">
              Explore practices, rituals, and guidance to support where you are right now.
            </p>
          )}
        </div>
      </div>

      {/* Featured Rituals Carousel */}
      <section className="pt-5 md:pt-10">
        {featuredError ? (
          <div className="flex items-center justify-center h-[300px] md:h-[620px] rounded-tl-[60px] rounded-br-[60px] bg-card text-primary/60">
            <p>Failed to load featured rituals</p>
          </div>
        ) : featuredLoading ? (
          <div className="h-[400px] md:h-[620px] rounded-tl-[60px] rounded-br-[60px] bg-card animate-pulse" />
        ) : featuredCardData.length > 0 ? (
          <FeaturedCarousel
            rituals={featuredCardData}
            className="pb-10 pl-5"
            itemsPerScreen={{ sm: 1, md: 1, lg: 1, xl: 1 }}
          />
        ) : (
          <div className="flex items-center justify-center h-[300px] md:h-[620px] rounded-tl-[60px] rounded-br-[60px] bg-card text-primary/60">
            <p className="text-sm">No featured rituals available yet</p>
          </div>
        )}
      </section>

      {/* Begin with Intention Section */}
      <IntentionsGrid
        intentions={intentionsData}
        onShowMore={() => navigate("/connect/intentions")}
      />

      {/* Trusted Ritualists Section */}
      {ritualistsError ? (
        <section className="pb-12.5 max-w-360 mx-auto px-5 md:px-17.5">
          <h2 className="text-xl md:text-2xl text-primary uppercase leading-7.5 font-semibold opacity-90 mb-5">
            Trusted Ritualists
          </h2>
          <div className="flex items-center justify-center h-[300px] text-primary/60">
            <p>Failed to load ritualists</p>
          </div>
        </section>
      ) : ritualistsLoading ? (
        <section className="pb-12.5 max-w-360 mx-auto px-5 md:px-17.5">
          <div className="flex gap-5 overflow-hidden">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="w-75 h-75 rounded-[20px] bg-card animate-pulse shrink-0" />
            ))}
          </div>
        </section>
      ) : trustedRitualists.length > 0 ? (
        <TrustedRitualists
          ritualists={trustedRitualists}
          onShowMore={() => navigate("/connect/ritualists")}
          onFollow={follow}
          onUnfollow={unfollow}
        />
      ) : null}

      {/* Explore Self-Guided Rituals Section */}
      <section
        id="explore-self-guided-rituals"
        className="pb-12.5 max-w-360 mx-auto px-5 md:px-17.5"
      >
        <h2
          id="explore-self-guided-rituals-listing"
          className="text-xl md:text-2xl text-primary uppercase leading-7.5 font-semibold mb-5 md:mb-7.5"
        >
          Explore Self-Guided Rituals
        </h2>

        {/* Search Bar */}
        <div className="mb-5 md:mb-3">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-[25px] h-[25px] text-primary opacity-90" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search..."
              className="w-full h-[50px] md:h-[65px] pl-[49px] pr-5 md:pr-52 rounded-[15px] bg-[rgba(242,240,235,0.05)] border border-[rgba(242,240,235,0.15)] text-primary placeholder:text-primary/90 text-sm leading-[25px] focus:outline-none focus:ring-1 focus:ring-brand-olive"
            />
            <div className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 items-center gap-1.25 text-primary text-sm font-semibold whitespace-nowrap opacity-90">
              <MapPin className="w-[25px] h-[25px]" />
              <AutocompleteInput
                label=""
                mode="full"
                placeholder="Any location"
                value={locationDisplay}
                onLocationSelect={handleLocationSelect}
                className="h-auto min-h-0 p-0 border-0 text-primary text-sm font-semibold bg-transparent"
              />
              {(activeFilters.city || activeFilters.state || activeFilters.country) && (
                <button
                  type="button"
                  onClick={handleLocationClear}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors"
                  aria-label="Clear location"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-[10px] mb-5 md:mb-6 overflow-x-auto scrollbar-hide md:pb-2">
          {/* Location -- mobile only */}
          <div className="shrink-0 md:hidden">
            <div className="flex items-center gap-1.25 text-primary text-sm font-semibold whitespace-nowrap opacity-90 px-3 h-[40px] rounded-[10px] border border-[#F2F0EB] p-[10px]">
              <MapPin className="w-4 h-4" />
              <AutocompleteInput
                label=""
                mode="full"
                placeholder="Any location"
                value={locationDisplay}
                onLocationSelect={handleLocationSelect}
                className="h-auto min-h-0 p-0 border-0 text-primary text-sm font-semibold bg-transparent"
              />
              {(activeFilters.city || activeFilters.state || activeFilters.country) && (
                <button
                  type="button"
                  onClick={handleLocationClear}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-primary/20 transition-colors"
                  aria-label="Clear location"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="shrink-0">
            <RadioPopover
              label="Sort by"
              options={SORT_RADIO_OPTIONS}
              value={activeFilters.sort || "newest"}
              onChange={val =>
                setActiveFilters(prev => ({
                  ...prev,
                  sort: val as "newest" | "trending" | "relevance",
                }))
              }
            />
          </div>

          <div className="shrink-0">
            <PriceFilterPopover
              selectedPrice={activeFilters.price ? [activeFilters.price] : []}
              priceRange={activeFilters.priceRange ?? [0, 100]}
              onApply={(price, priceRange) => {
                setActiveFilters(prev => {
                  const next = { ...prev }
                  const filtered = price.filter(v => v !== "all")
                  const isRangeActive = priceRange[0] > 0 || priceRange[1] < 100

                  if (filtered.length > 0) {
                    next.price = filtered[0] as "free" | "paid"
                  } else {
                    delete next.price
                  }

                  if (isRangeActive) {
                    next.priceRange = priceRange
                  } else {
                    delete next.priceRange
                  }

                  return next
                })
              }}
            />
          </div>

          <div className="shrink-0">
            <SingleToggleChipPopover
              label="Format"
              options={FORMAT_CHIP_OPTIONS}
              selected={activeFilters.format}
              onApply={val => {
                setActiveFilters(prev => {
                  const next = { ...prev }
                  if (val) {
                    next.format = val as RitualType
                  } else {
                    delete next.format
                  }
                  return next
                })
              }}
            />
          </div>

          <div className="shrink-0">
            <ToggleChipPopover
              label="Intention"
              options={intentionsList.map(i => ({ value: i.id, label: i.name }))}
              selected={activeFilters.intentions ? activeFilters.intentions.split(",") : []}
              onApply={vals => {
                setActiveFilters(prev => {
                  const next = { ...prev }
                  if (vals.length > 0) {
                    next.intentions = vals.join(",")
                  } else {
                    delete next.intentions
                  }
                  return next
                })
              }}
            />
          </div>

          <div className="shrink-0">
            <ToggleChipPopover
              label="Spiritual Traditions"
              options={traditionsList.map(t => ({ value: t.id, label: t.title }))}
              selected={activeFilters.traditions ? activeFilters.traditions.split(",") : []}
              onApply={vals => {
                setActiveFilters(prev => {
                  const next = { ...prev }
                  if (vals.length > 0) {
                    next.traditions = vals.join(",")
                  } else {
                    delete next.traditions
                  }
                  return next
                })
              }}
              maxWidth="480px"
            />
          </div>

          <div className="shrink-0">
            <SingleToggleChipPopover
              label="Duration"
              options={DURATION_CHIP_OPTIONS}
              selected={activeFilters.duration}
              onApply={val => {
                setActiveFilters(prev => {
                  const next = { ...prev }
                  if (val) {
                    next.duration = val as "short" | "medium" | "long"
                  } else {
                    delete next.duration
                  }
                  return next
                })
              }}
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="shrink-0 flex items-center gap-[5px] pl-[10px] text-[14px] font-semibold text-brand-terracotta hover:text-brand-terracotta/80 transition-colors"
            >
              <span className="size-[20px] rounded-full border border-brand-terracotta flex items-center justify-center">
                <X className="size-[12px]" />
              </span>
              Reset All
            </button>
          )}
        </div>

        {/* Rituals Grid */}
        {exploreError ? (
          <div className="flex flex-col items-center justify-center py-20 text-primary/60">
            <p className="text-lg">Failed to load rituals</p>
            <p className="text-sm mt-2">Please try again later</p>
          </div>
        ) : exploreLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[440px] rounded-tl-[40px] rounded-br-[40px] bg-card animate-pulse"
              />
            ))}
          </div>
        ) : exploreCardData.length > 0 ? (
          <RitualGrid
            rituals={exploreCardData}
            isLoading={isFetchingNextPage}
            hasMore={hasNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-primary/60">
            <p className="text-lg">No rituals found</p>
            <p className="text-sm mt-2">Try adjusting your search or filters</p>
          </div>
        )}

        {/* Load More Button */}
        {hasNextPage && !isFetchingNextPage && !exploreLoading && (
          <div className="flex justify-center mt-10 mb-[90px]">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
            >
              Load more
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
